/**
 * Shared Amadeus Console component
 * Used by both lawyer/console.tsx and tools/console.tsx
 */
import { useState, useRef, useEffect } from 'react'
import { Terminal, Loader2, Wifi, WifiOff } from 'lucide-react'
import { useAmadeusWebSocket, type QueueProgress } from '@/contexts/AmadeusWebSocketContext'

/**
 * WebSocket status indicator component
 */
function StatusIndicator({ wsStatus }: { wsStatus: string }) {
  const isConnected = wsStatus === 'connected'
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {isConnected ? (
        <>
          <Wifi className="h-3.5 w-3.5 text-green-500" />
          <span className="text-green-600">Connected</span>
        </>
      ) : (
        <>
          <WifiOff className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-amber-600">
            {wsStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
          </span>
        </>
      )}
    </div>
  )
}

export function AmadeusConsole() {
  const [displayContent, setDisplayContent] = useState<string>('')
  const [command, setCommand] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [queueStatus, setQueueStatus] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Use the shared Amadeus WebSocket connection
  const { status: wsStatus, sendCommand } = useAmadeusWebSocket()

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleProgress = (progress: QueueProgress) => {
    if (progress.type === 'queue_status' && progress.queue_position && progress.queue_position > 1) {
      setQueueStatus(`Queued (position ${progress.queue_position})...`)
    } else if (progress.type === 'processing') {
      setQueueStatus('Executing...')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedCommand = command.trim()
    if (!trimmedCommand || isLoading) return

    // Check WebSocket connection
    if (wsStatus !== 'connected') {
      setError('WebSocket not connected. Please wait for reconnection.')
      return
    }

    setCommand('')
    setIsLoading(true)
    setError(null)
    setQueueStatus('Queuing...')
    // Clear display immediately to show we're processing
    setDisplayContent('')

    try {
      const result = await sendCommand(trimmedCommand, handleProgress)

      if (result.response) {
        setDisplayContent(result.response)
        setError(null)
      } else {
        setError('No response received')
        setDisplayContent('')
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      setDisplayContent('')
    } finally {
      setIsLoading(false)
      setQueueStatus(null)
      inputRef.current?.focus()
    }
  }

  // Handle clicking on terminal area to focus input
  const handleTerminalClick = () => {
    inputRef.current?.focus()
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Terminal className="h-8 w-8 text-action-500" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Console</h1>
            <p className="text-slate-500">Amadeus Cryptic Commands ausführen</p>
          </div>
        </div>
        <StatusIndicator wsStatus={wsStatus} />
      </div>

      {/* Terminal Container - Fixed width based on 80 chars */}
      <div
        className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col"
        style={{
          width: 'fit-content',
          minWidth: '720px', // ~80 chars at 14px monospace + padding
        }}
        onClick={handleTerminalClick}
      >
        {/* Display Area - Fixed height for 24 lines */}
        <div
          className="p-4 font-mono text-sm overflow-auto bg-white"
          style={{
            height: '420px', // ~24 lines at 1.4rem line height + padding
            minHeight: '420px',
          }}
        >
          {/* Loading state */}
          {isLoading && (
            <div className="text-slate-400 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{queueStatus || 'Executing...'}</span>
            </div>
          )}

          {/* Error display */}
          {!isLoading && error && (
            <div className="text-red-600">
              Error: {error}
            </div>
          )}

          {/* Response display */}
          {!isLoading && !error && displayContent && (
            <pre
              className="text-slate-900 whitespace-pre"
              style={{
                lineHeight: '1.4',
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                fontSize: '13px',
              }}
            >
              {displayContent}
            </pre>
          )}

          {/* Empty state */}
          {!isLoading && !error && !displayContent && (
            <div className="text-slate-400 select-none">
              Geben Sie einen Amadeus-Befehl ein und drücken Sie Enter.
              <br />
              Beispiele: RT, DOLH123/15DEC, ANMUCFRA, GD, PD
            </div>
          )}
        </div>

        {/* Input Area */}
        <form onSubmit={handleSubmit} className="border-t border-slate-200 p-4 bg-slate-50">
          <div className="flex items-center gap-2 font-mono">
            <span className="text-action-600 font-semibold">&gt;</span>
            <input
              ref={inputRef}
              type="text"
              value={command}
              onChange={(e) => setCommand(e.target.value.toUpperCase())}
              disabled={isLoading || wsStatus !== 'connected'}
              placeholder={wsStatus === 'connected' ? 'Enter command...' : 'Connecting...'}
              className="flex-1 bg-transparent border-none outline-none text-slate-900 placeholder:text-slate-400 text-sm uppercase disabled:opacity-50"
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                fontSize: '13px',
              }}
              autoComplete="off"
              spellCheck={false}
            />
            {isLoading && (
              <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
            )}
          </div>
        </form>
      </div>
    </>
  )
}
